import { adminLogin } from 'apis';
import { useRef, useState } from 'react';

type Step = 'password' | 'pin';

const AdminLoginComponent = () => {
    const [step, setStep] = useState<Step>('password');
    const [password, setPassword] = useState<string>('');
    const [pin, setPin] = useState<string[]>(['', '', '', '', '', '']);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const pinRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handlePasswordNext = () => {
        if (!password.trim()) {
            setError('비밀번호를 입력해주세요.');
            return;
        }
        setError('');
        setStep('pin');
        setTimeout(() => pinRefs.current[0]?.focus(), 50);
    };

    const handlePinChange = (index: number, value: string) => {
        // 숫자만 허용
        if (value && !/^\d$/.test(value)) return;

        const newPin = [...pin];
        newPin[index] = value;
        setPin(newPin);
        setError('');

        if (value && index < 5) {
            pinRefs.current[index + 1]?.focus();
        }

        // 6자리 모두 입력 완료 시 자동 로그인
        if (value && index === 5) {
            const fullPin = newPin.join('');
            if (fullPin.length === 6) {
                handleLogin(fullPin);
            }
        }
    };

    const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !pin[index] && index > 0) {
            pinRefs.current[index - 1]?.focus();
        }
        if (e.key === 'Enter') {
            const fullPin = pin.join('');
            if (fullPin.length === 6) {
                handleLogin(fullPin);
            }
        }
    };

    const handlePinPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 0) return;

        const newPin = [...pin];
        for (let i = 0; i < 6; i++) {
            newPin[i] = pasted[i] || '';
        }
        setPin(newPin);

        if (pasted.length === 6) {
            handleLogin(pasted);
        } else {
            pinRefs.current[Math.min(pasted.length, 5)]?.focus();
        }
    };

    const handleLogin = async (pinCode: string) => {
        setLoading(true);
        setError('');
        try {
            await adminLogin(password, pinCode);
        } catch (err) {
            setError('비밀번호 또는 보안코드가 올바르지 않습니다.');
            setPin(['', '', '', '', '', '']);
            setTimeout(() => pinRefs.current[0]?.focus(), 50);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handlePasswordNext();
    };

    const handleBack = () => {
        setStep('password');
        setPin(['', '', '', '', '', '']);
        setError('');
    };

    return (
        <div className="w-full h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #312E81 0%, #4F46E5 50%, #7C3AED 100%)' }}>
            <div className="w-full max-w-[400px] mx-4">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="px-8 pt-10 pb-6 text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-50 rounded-2xl mb-4">
                            <span className="text-2xl">{step === 'password' ? '🔐' : '🔢'}</span>
                        </div>
                        <h1 className="text-xl font-extrabold text-achromatic-900 mb-1">
                            {step === 'password' ? '관리자 로그인' : '보안코드 입력'}
                        </h1>
                        <p className="text-[13px] text-achromatic-400">
                            {step === 'password' ? 'Tipot Admin Console' : '6자리 보안코드를 입력해주세요'}
                        </p>
                    </div>

                    {/* Form */}
                    <div className="px-8 pb-10">
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-[13px] text-red-600 font-medium text-center">
                                {error}
                            </div>
                        )}

                        {step === 'password' ? (
                            <>
                                <div className="mb-5">
                                    <label className="block text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider mb-2">비밀번호</label>
                                    <input
                                        type="password"
                                        className="w-full px-4 h-12 border border-achromatic-200 rounded-xl text-sm text-achromatic-900 font-medium focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all placeholder:text-achromatic-300"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={handlePasswordKeyDown}
                                        placeholder="관리자 비밀번호를 입력하세요"
                                        autoFocus
                                    />
                                </div>
                                <button
                                    className="w-full h-12 bg-primary-500 text-white rounded-xl text-sm font-bold hover:bg-primary-600 transition-all active:scale-[0.98]"
                                    onClick={handlePasswordNext}
                                >
                                    다음
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="mb-5">
                                    <label className="block text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider mb-3">보안코드</label>
                                    <div className="flex gap-2 justify-center" onPaste={handlePinPaste}>
                                        {pin.map((digit, i) => (
                                            <input
                                                key={i}
                                                ref={(el) => { pinRefs.current[i] = el; }}
                                                type="password"
                                                inputMode="numeric"
                                                pattern="[0-9]"
                                                maxLength={1}
                                                className="w-12 h-14 text-center text-xl font-bold border border-achromatic-200 rounded-xl text-achromatic-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                                                value={digit}
                                                onChange={(e) => handlePinChange(i, e.target.value)}
                                                onKeyDown={(e) => handlePinKeyDown(i, e)}
                                                disabled={loading}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {loading && (
                                    <div className="text-center text-sm text-achromatic-400 mb-4">인증 중...</div>
                                )}

                                <button
                                    className="w-full h-10 text-achromatic-400 text-sm font-medium hover:text-achromatic-600 transition-all"
                                    onClick={handleBack}
                                    disabled={loading}
                                >
                                    ← 뒤로가기
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <p className="text-center text-[11px] text-white/50 mt-6">© 주식회사 젤리펀치 · Tipot Admin</p>
            </div>
        </div>
    );
};

export default AdminLoginComponent;